// An "Alert" modal, as in, alert(), meaning, requires
// ... confirmation before performing the user intented action

import { ReactNode } from "react";
import ReactModal from "react-modal";

import { CaseDiagnosis, User, Case } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { useAppSelector } from "store/hooks";

import { createListString, isModifyingReport } from "../dialogs/utils";

import "./AlertModal.css";

interface AlertModalProps {
  title?: string;
  message?: string;
  action?: Function;
  actionLabel?: string;
  contents?: ReactNode;

  isOpen: boolean;
  setIsOpen: Function;
}
export default function AlertModal({
  title,
  message,
  contents,
  action,
  actionLabel,
  isOpen,
  setIsOpen,
}: AlertModalProps) {
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      className="Modal Modal--centre"
      overlayClassName="Overlay"
    >
      <div className="AlertModal">
        {title && (
          <header>
            <h2>{title}</h2>
          </header>
        )}
        {message && <p>{message}</p>}
        {contents}
        <footer>
          <button
            className="btn btn--link AlertModal__close"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            {action ? "Cancel" : "OK"}
          </button>
          {action && (
            <button
              className="btn btn--red AlertModal__action"
              onClick={() => {
                action();
                setIsOpen(false);
              }}
              type="button"
              data-testid="alert-modal-action"
            >
              {actionLabel ?? "OK"}
            </button>
          )}
        </footer>
      </div>
    </ReactModal>
  );
}

export const SignoutAlertModal = ({
  signOutCase,
  signoutAlert,
  setSignoutAlert,
  caseDetailObject,
  invalidReasons,
  softDiagnosisValidations,
  currentUser,
}: {
  signoutAlert: boolean;
  setSignoutAlert: Function;
  signOutCase: Function;
  caseDetailObject: CaseDetailState;
  invalidReasons?: (string | boolean)[];
  softDiagnosisValidations?: string[];
  currentUser?: User;
}) => {
  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);

  const hasInvalidReasons = !!invalidReasons?.length;
  const isWorking = caseDetailObject.status === Case.status.WORKING;
  const isModifying = isModifyingReport(
    caseDetailObject,
    features,
    currentUser
  );
  const message = createListString(
    [
      ...(isWorking ? ["is not in Active status"] : []),
      ...(hasInvalidReasons ? ["may have missing information"] : []),
      ...(softDiagnosisValidations?.length
        ? ["may have missing entries in diagnoses"]
        : []),
    ].filter((r) => r)
  );

  return (
    <AlertModal
      title={`${caseDetailObject.status} Case Warning`}
      message={`This case ${message}. Are you sure you want to ${
        caseDetailObject.is_screening
          ? "Screen"
          : isModifying?.source === CaseDiagnosis.source.CORRECTION
          ? "correct the report for"
          : "Sign Out"
      } this case?`}
      action={signOutCase}
      actionLabel={
        caseDetailObject.is_screening
          ? "Screen"
          : isModifying?.source === CaseDiagnosis.source.CORRECTION
          ? "Correct Report"
          : "Sign Out"
      }
      isOpen={signoutAlert}
      setIsOpen={setSignoutAlert}
    />
  );
};
